import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    Public as RegionIcon,
    Hub as ZoneIcon,
    LinkOff as DisconnectedIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import RegionList from './regions/RegionList';
import { regionService } from '../../services/regionService';

function RegionManagement() {
    const [stats, setStats] = useState(null);
    const { showSuccess, showError } = useApiHandler();

    useEffect(() => {
        loadRegionStats();
    }, []);

    const loadRegionStats = async () => {
        try {
            const response = await regionService.getRegionStats();
            
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            showError('Error loading region statistics');
        }
    };

    const refreshStats = () => {
        loadRegionStats();
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Region Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage delivery regions and their zone connections
                </Typography>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <RegionIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Total Regions</Typography>
                                </Box>
                                <Typography variant="h4" color="primary" gutterBottom>
                                    {stats.total}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label={`${stats.active} Active`} 
                                        color="success" 
                                        size="small" 
                                    />
                                    <Chip 
                                        label={`${stats.inactive} Inactive`} 
                                        color="default" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ZoneIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Connected to Zones</Typography>
                                </Box>
                                <Typography variant="h4" color="success.main" gutterBottom>
                                    {stats.with_zones}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label="Zone Linked" 
                                        color="success" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <DisconnectedIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Not Connected</Typography>
                                </Box>
                                <Typography variant="h4" color="warning.main" gutterBottom>
                                    {stats.without_zones}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip 
                                        label="No Zone Links" 
                                        color="warning" 
                                        size="small" 
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Region Management */}
            <Paper sx={{ width: '100%' }}>
                <RegionList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
}

export default RegionManagement; 